import React from "react"

import { Table } from "@UILibrary/Table"
import { Pill } from "@UILibrary/Pill"
import { Pagination } from "@UILibrary/Pagination"
import { Icon } from "@UILibrary/Icon"
import { ArchiveScenarioModal } from "@components/Modal/ArchiveScenarioModal"

import useModalVisibilityControls from '@hooks/useModalVisibilityControls'
import { getLongDateFromDate } from '@utils/dateParser'
import Routes from "@utils/routes"
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useScenarioListQuery } from '@api/scenarios/queries/useScenarioListQuery';
import { humanizeString } from "@utils/stringMinipulation"

import "./scenarios.scss"

type Scenario = {
  id: string,
  name: string,
  planType: "implemented" | "archived" | "scenario" | "joint_plan" | "historic",
  status: string,
  createdAt: string,
  updatedAt: string,
}

type ScenariosProps = {
  tenant: {
    slug: string
  },
  client: {
    id: string
  },
  scenarios: Scenario[],
  jointScenario?: Scenario,
  onScenarioNavigate: (scenario: Scenario) => void,
  onUpdateScenario: (scenarioId: Scenario['id'], payload: any) => Promise<{ success: boolean }>
}

const baseClass = "scenarios__clients"

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      refetchOnMount: false,
      useErrorBoundary: true
    },
  },
})

const ScenariosContext = (props: ScenariosProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ScenariosWrapper {...props} />
    </QueryClientProvider>
  )
};

const ScenariosWrapper = (props: ScenariosProps) => {
  const scenariosList = useScenarioListQuery({
    tenantSlug: props.tenant.slug,
    clientId: props.client.id,
    queryKey: "scenarios-list",
    initialData: undefined
  })

  const onUpdateScenario = async (scenarioId: Scenario['id'], payload: any) => {
    try {
      const result = await scenariosList.actions.update({ id: scenarioId, ...payload })

      scenariosList.actions.refetch()
      return { success: true };
    } catch (e) {
      return { success: false, message: e.message }
    }
  }

  const onScenarioNavigate = (scenario: Scenario) => {
    if (scenario.planType == "joint_plan") {
      window.location.href = Routes.jointScenarioPath({
        tenantSlug: props.tenant.slug,
        clientId: props.client.id,
        jointScenarioId: scenario.id
      })
    } else {
      window.location.href = Routes.clientScenarioPath({
        tenantSlug: props.tenant.slug,
        clientId: props.client.id,
        scenarioId: scenario.id
      })
    }
  }


  return (
    <Scenarios {...props}
      scenarios={scenariosList.data?.scenarios ?? []}
      jointScenario={scenariosList.data?.joint_scenario ?? undefined}
      onUpdateScenario={onUpdateScenario}
      onScenarioNavigate={onScenarioNavigate}
    />
  )
}

const defaultSort = (a: Scenario, b: Scenario) => {
  if (b.planType == "implemented") return 1;
  if (a.planType == "implemented") return -1;
  if (a.planType == "joint_plan") return -1;
  if (b.planType == "joint_plan") return 1;

  return b.planType.localeCompare(a.planType)
}

const Scenarios = ({
  tenant,
  client,
  scenarios = [],
  jointScenario,
  onScenarioNavigate,
  onUpdateScenario
}: ScenariosProps) => {
  // state
  const [archiveTarget, setArchiveTarget] = React.useState<Scenario | null>(null)
  const [searchTerm, setSearchTerm] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [filteredDataLength, setFilteredDataLength] = React.useState(scenarios.length)

  const itemsPerPage = 10;
  const data = ([...scenarios, jointScenario].filter(v => !!v) as Scenario[]);

  // modal controls
  const {
    modalVisible: archiveVisible,
    openModal: openArchive,
    closeModal: closeArchive
  } = useModalVisibilityControls()

  // handlers
  const openArchiveFor = (scenario: Scenario) => {
    setArchiveTarget(scenario)
    openArchive()
  }

  const archiveScenario = () => {
    if (!archiveTarget) return;

    return onUpdateScenario(archiveTarget.id, { plan_type: "archived" });
  }

  // components
  const ScenarioStatus = (scenario: Scenario) => {
    const isImplemented = scenario.planType == "implemented";
    const pillText = humanizeString(
      isImplemented ? "Implemented" :
      scenario.planType == "joint_plan" ? scenario.planType :
      scenario.planType == "scenario" ? scenario.status : scenario.planType
    )
    return (
      <Pill
        size="small"
        color="white"
        iconBefore={isImplemented ? <Icon name="star" size="small" /> : ""}
        className={`${baseClass}--col-status`}>{pillText}</Pill>
    )
  }

  const canViewScenario = (scenario?: Scenario) => {
    return scenario?.planType != "archived"
  }

  const canArchiveScenario = (scenario?: Scenario) => {
    return scenario?.planType != "archived" && scenario?.planType != "implemented" && scenario?.planType != "joint_plan" && scenario?.planType != "historic"
  }

  // Destination for a scenario row — same URL onScenarioNavigate uses, but as a
  // real href so the name reads/behaves like a standard hyperlink.
  const scenarioHref = (item: Scenario) =>
    item.planType == "joint_plan"
      ? Routes.jointScenarioPath({ tenantSlug: tenant.slug, clientId: client.id, jointScenarioId: item.id })
      : Routes.clientScenarioPath({ tenantSlug: tenant.slug, clientId: client.id, scenarioId: item.id })

  // Scenario name: a hyperlink when viewable (shared .wb-clients__link look with
  // the clients/prospects tables), plain text otherwise.
  const ScenarioName = (item: Scenario) =>
    canViewScenario(item) ? (
      <a href={scenarioHref(item)} className="wb-clients__link">{item.name}</a>
    ) : (
      <span>{item.name}</span>
    )

  const tableColumns = [
    { label: 'Name', key: 'name', sortable: true, render: ScenarioName },
    { label: 'Status', key: 'status', sortable: true, render: ScenarioStatus },
    { label: 'Created at', key: 'createdAt', sortable: true, render: item => getLongDateFromDate(item.createdAt) },
    { label: 'Updated at', key: 'updatedAt', sortable: true, render: item => getLongDateFromDate(item.updatedAt) },
  ]

  // Row "Actions" dropdown — always present (matching the old always-visible
  // menu); Archive is disabled when the scenario can't be archived.
  const rowActions = (item: Scenario) => [
    { label: 'Archive', danger: true, disabled: !canArchiveScenario(item), onClick: () => openArchiveFor(item) },
  ]

  // effects
  // Reset to the first page when the search term changes (the old useTable did
  // this internally; the Table now takes the page as a controlled prop).
  React.useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  return (
    <div className={`${baseClass} wb-clients-redesign`}>
      <div className="wb-clients__header">
        <div className="wb-clients__heading">
          <h1>Scenarios</h1>
          <div className="wb-clients__subtitle">
            {data.length} {data.length === 1 ? 'scenario' : 'scenarios'}
          </div>
        </div>
        <div className="wb-clients__toolbar">
          <form className="wb-clients__search" onSubmit={(e) => e.preventDefault()}>
            <div className="wb-clients__search-wrap">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#98A2B3" strokeWidth="1.6" strokeLinecap="round">
                <circle cx="7" cy="7" r="4.5" />
                <path d="M14 14l-3.2-3.2" />
              </svg>
              <input
                type="search"
                className="wb-clients__search-input"
                placeholder="Search scenarios"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button type="submit" className="wb-clients__search-btn">Search</button>
          </form>
        </div>
      </div>

      <Table
        data={data}
        columns={tableColumns}
        rowActions={rowActions}
        itemsPerPage={itemsPerPage}
        searchTerm={searchTerm}
        page={currentPage}
        onPageChange={setCurrentPage}
        onFilteredCountChange={setFilteredDataLength}
        defaultSort={defaultSort}
        pager="none"
      />

      <ArchiveScenarioModal
        open={archiveVisible}
        close={closeArchive}
        updateScenario={archiveScenario}
        scenario={archiveTarget ?? undefined}
      />

      <div className={`${baseClass}__pagination`}>
        <Pagination
          className={`${baseClass}__pagination-controls`}
          count={filteredDataLength}
          current={currentPage}
          perPage={itemsPerPage}
          onPaginate={setCurrentPage}
        />
      </div>
    </div>
  )
}

export { ScenariosContext as Scenarios }
